/**
 * api.js — Chamadas ao backend FastAPI.
 *
 * O proxy definido no package.json (http://localhost:8000) encaminha
 * automaticamente todos os pedidos /api/* para o backend em desenvolvimento.
 */

const BASE_URL = "/api";

// "Ver como PM" — só tem efeito para utilizadores admin (o backend ignora
// para qualquer outro role). Aplicado automaticamente a todos os pedidos.
let comoPmId = null;

export function setViewAsPm(pmId) {
  comoPmId = pmId;
}

export function getViewAsPm() {
  return comoPmId;
}

async function _fetch(path, options = {}) {
  let url = `${BASE_URL}${path}`;
  if (comoPmId != null) {
    url += (url.includes("?") ? "&" : "?") + `como_pm=${encodeURIComponent(comoPmId)}`;
  }

  const response = await fetch(url, {
    headers: { "Content-Type": "application/json", ...options.headers },
    ...options,
  });

  if (!response.ok) {
    const detail = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(detail.detail || `Erro ${response.status}`);
  }

  return response.json();
}

// ---------------------------------------------------------------------------
// Autenticação
// ---------------------------------------------------------------------------

export const fetchMe = async () => {
  const response = await fetch("/auth/me");
  if (response.status === 401) return null;
  if (!response.ok) throw new Error("Erro ao verificar sessão");
  return response.json();
};

// ---------------------------------------------------------------------------
// Sistema
// ---------------------------------------------------------------------------

export const fetchHealth = () => _fetch("/health");

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------

export const fetchOverview = () => _fetch("/overview");

// ---------------------------------------------------------------------------
// Account
// ---------------------------------------------------------------------------

export const fetchAccounts = () => _fetch("/accounts");

export const fetchAccount = (accountName) =>
  _fetch(`/account/${encodeURIComponent(accountName)}`);

// ---------------------------------------------------------------------------
// Marca
// ---------------------------------------------------------------------------

export const fetchMarcas = () => _fetch("/marcas");

export const fetchMarcasSaude = () => _fetch("/marcas-saude");

export const fetchMarca = (marcaId) =>
  _fetch(`/marca/${encodeURIComponent(marcaId)}`);

// ---------------------------------------------------------------------------
// Dar Atenção / Bottlenecks
// ---------------------------------------------------------------------------

export const fetchDarAtencao = (params = {}) => {
  const query = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v != null))
  ).toString();
  return _fetch(`/dar-atencao${query ? `?${query}` : ""}`);
};

// ---------------------------------------------------------------------------
// Admin — Configurações
// ---------------------------------------------------------------------------

export const fetchConfiguracoes = () => _fetch("/admin/configuracoes");

export const updateConfiguracao = (chave, valor) =>
  _fetch(`/admin/configuracoes/${encodeURIComponent(chave)}`, {
    method: "PUT",
    body: JSON.stringify({ valor: String(valor) }),
  });

export const updateScorePesos = (pesos) =>
  _fetch("/admin/score-pesos", {
    method: "PUT",
    body: JSON.stringify(pesos),
  });

// ---------------------------------------------------------------------------
// Admin — Sincronização
// ---------------------------------------------------------------------------

export const descobrirPlanners = () => _fetch("/admin/discover", { method: "POST" });

export const forcaSync = () => _fetch("/admin/sync", { method: "POST" });

export const fetchSyncLog = () => _fetch("/admin/sync/log");

// ---------------------------------------------------------------------------
// Admin — Utilizadores
// ---------------------------------------------------------------------------

export const fetchUtilizadores = () => _fetch("/admin/utilizadores");

export const updateUtilizador = (userId, updates) =>
  _fetch(`/admin/utilizadores/${encodeURIComponent(userId)}`, {
    method: "PUT",
    body: JSON.stringify(updates),
  });

export const fetchConvites = () => _fetch("/admin/convites");

export const createConvite = (email, role) =>
  _fetch("/admin/convites", { method: "POST", body: JSON.stringify({ email, role }) });

export const deleteConvite = (email) =>
  _fetch(`/admin/convites/${encodeURIComponent(email)}`, { method: "DELETE" });

export const fetchLinksAcesso = () => _fetch("/admin/links-acesso");

export const createLinkAcesso = (nome, diasValidade) =>
  _fetch("/admin/links-acesso", {
    method: "POST",
    body: JSON.stringify({ nome, dias_validade: diasValidade }),
  });

export const revogarLinkAcesso = (id) =>
  _fetch(`/admin/links-acesso/${id}/revogar`, { method: "PATCH" });

// ---------------------------------------------------------------------------
// Project Managers
// ---------------------------------------------------------------------------

export const fetchEstrutura = (params = {}) => {
  const query = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ""))
  ).toString();
  return _fetch(`/estrutura${query ? `?${query}` : ""}`);
};

export const fetchHeads = () => _fetch("/heads");

export const createHead = (data) =>
  _fetch("/admin/heads", { method: "POST", body: JSON.stringify(data) });

export const updateHead = (id, data) =>
  _fetch(`/admin/heads/${id}`, { method: "PUT", body: JSON.stringify(data) });

export const deleteHead = (id) =>
  _fetch(`/admin/heads/${id}`, { method: "DELETE" }).catch(() => null);

export const fetchProjectManagers = () => _fetch("/project-managers");

export const fetchProjectManager = (pmId) => _fetch(`/project-manager/${pmId}`);

export const createProjectManager = (data) =>
  _fetch("/admin/project-managers", { method: "POST", body: JSON.stringify(data) });

export const updateProjectManager = (pmId, data) =>
  _fetch(`/admin/project-managers/${pmId}`, { method: "PUT", body: JSON.stringify(data) });

export const deleteProjectManager = (pmId) =>
  _fetch(`/admin/project-managers/${pmId}`, { method: "DELETE" }).catch(() => null);

// ---------------------------------------------------------------------------
// Operational Managers
// ---------------------------------------------------------------------------

export const fetchOperationalManagers = () => _fetch("/operational-managers");

export const fetchOperationalManager = (omId) => _fetch(`/operational-manager/${omId}`);

export const createOperationalManager = (data) =>
  _fetch("/admin/operational-managers", { method: "POST", body: JSON.stringify(data) });

export const updateOperationalManager = (omId, data) =>
  _fetch(`/admin/operational-managers/${omId}`, { method: "PUT", body: JSON.stringify(data) });

export const deleteOperationalManager = (omId) =>
  _fetch(`/admin/operational-managers/${omId}`, { method: "DELETE" }).catch(() => null);

export const resolverIdsOMs = () =>
  _fetch("/admin/operational-managers/resolver-ids", { method: "POST" });

// ---------------------------------------------------------------------------
// Admin — Planners
// ---------------------------------------------------------------------------

export const fetchAdminPlanners = () => _fetch("/admin/planners");

export const fetchUnassignedPlanners = () => _fetch("/admin/planners/unassigned");

export const bulkAssignPlanners = (data) =>
  _fetch("/admin/planners/bulk-assign", { method: "POST", body: JSON.stringify(data) });

export const assignPlanner = (planId, data) =>
  _fetch(`/admin/planners/${encodeURIComponent(planId)}/assign`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
